// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — drawing category descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "drawing" — base geometric primitives that author new entities
// from scratch (LINE, CIRCLE, ARC, PLINE, RECTANG, HATCH, REVCLOUD).
//
// Sources of truth used to author each descriptor (see plan):
//   • command type literals: packages/contracts/src/editor.ts:259-498
//   • aliases: apps/web/src/app/scene/draftingHelpers.ts:340-441
//     (normalizeDraftCommand switch). RU aliases that the switch does not
//     yet recognise are still authored here so the AI / help layer can
//     surface AutoCAD-CIS muscle-memory; the validator's alias-parity
//     check warns (does not error) when a registry alias is absent from
//     the switch — those are tracked as a follow-up to widen the switch.
//   • i18n labels: apps/web/src/app/i18n/messages.ts (command.*.label)
//   • prompt keys: apps/web/src/app/i18n/messages.ts (prompt.*)
//   • draft step ids: apps/web/src/app/scene/draftingHelpers.ts:120-329
//   • initial step mapping: apps/web/src/app/scene/draftingHelpers.ts:509-575
//
// All seven commands are reducer-implemented (geometryReducer.ts dispatches
// the matching geometry/create-* literals), so every descriptor here ships
// with status: "implemented" and full RU+EN help.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_GEOMETRY = "apps/web/src/app/state/geometryReducer.ts" as const;

// ───────────────────────────── LINE ───────────────────────────────────────
const LINE: CommandDescriptor = {
  id: "LINE",
  category: "drawing",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["LINE", "L"], ru: ["ОТРЕЗОК"] },
  commandTypeLiterals: ["geometry/create-line"],
  draftCommandId: "LINE",
  draftSteps: [
    {
      stepId: "line-first-point",
      promptKeyRu: "prompt.line.first",
      promptKeyEn: "prompt.line.first",
      expects: "point",
    },
    {
      stepId: "line-next-point",
      promptKeyRu: "prompt.line.next",
      promptKeyEn: "prompt.line.next",
      expects: "point",
      keywords: ["C", "U"],
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства (model / paper layout), в котором создаётся отрезок.",
        en: "Space identifier (model / paper layout) where the line is created.",
      },
    },
    {
      name: "start",
      type: "point3d",
      required: true,
      description: {
        ru: "Начальная точка отрезка (x, y, z).",
        en: "Start point of the line (x, y, z).",
      },
    },
    {
      name: "end",
      type: "point3d",
      required: true,
      description: {
        ru: "Конечная точка отрезка (x, y, z).",
        en: "End point of the line (x, y, z).",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт отрезок прямой между двумя точками.",
      long: "Команда ОТРЕЗОК создаёт отдельные сегменты прямой линии между указанными точками. Каждый сегмент является самостоятельной сущностью LINE и может быть отредактирован независимо. После указания первой точки последующие точки порождают новые сегменты, образующие непрерывную ломаную (но не PLINE). Для создания цельной полилинии используйте ПЛИНИЯ. На втором шаге введите C для замыкания цепочки сегментов отрезком до начальной точки или U для отмены последнего сегмента.",
      examples: ["ОТРЕЗОК 0,0 100,0 100,50", "L 0,0 @100<0"],
      seeAlso: ["PLINE", "RECTANG"],
    },
    en: {
      short: "Creates a straight line segment between two points.",
      long: "The LINE command creates individual straight-line segments between the points you pick. Each segment is a standalone LINE entity and can be edited independently. After the first pick, subsequent picks add more segments forming a continuous chain (but not a single PLINE entity). For a single polyline entity use PLINE. On the second step, type C to close the chain with a final segment back to the start, or U to undo the previous segment.",
      examples: ["LINE 0,0 100,0 100,50", "L 0,0 @100<0"],
      seeAlso: ["PLINE", "RECTANG"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.line.label",
    labelKeyEn: "command.line.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "L",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-line", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── POINT ──────────────────────────────────────
// AutoCAD POINT — one of the simplest primitives: a single (x, y) location
// rendered with a marker shape selected by the PDMODE system variable.
// The command has a single click step (`point-location`). Aliases include
// the AutoCAD CIS muscle memory (ТОЧКА, Т) plus the english-alias PO.
const POINT: CommandDescriptor = {
  id: "POINT",
  category: "drawing",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: { en: ["POINT", "PO"], ru: ["ТОЧКА", "Т"] },
  commandTypeLiterals: ["geometry/create-point"],
  draftCommandId: "POINT",
  draftSteps: [
    {
      stepId: "point-location",
      promptKeyRu: "prompt.point.location",
      promptKeyEn: "prompt.point.location",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся точка.",
        en: "Space identifier where the point is created.",
      },
    },
    {
      name: "location",
      type: "point3d",
      required: true,
      description: {
        ru: "Положение точки (x, y, z).",
        en: "Location of the point (x, y, z).",
      },
    },
    {
      name: "rotation",
      type: "number",
      required: false,
      defaultValue: "0",
      description: {
        ru: "Угол поворота относительно оси X UCS (в градусах, DXF-код 50) для маркеров PDMODE. По умолчанию 0.",
        en: "Rotation about the UCS X axis (degrees, DXF group 50) used by PDMODE marker shapes. Defaults to 0.",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт точку (POINT) в указанном положении.",
      long: "Команда ТОЧКА создаёт примитив POINT — самостоятельную сущность с одним 3D-координатным положением. Внешний вид точки на чертеже определяется системной переменной PDMODE (0 — одиночная точка, 2 — крестик, 3 — крест X, 4 — вертикальная чёрточка; +32 — окружность, +64 — квадрат), а размер — переменной PDSIZE. Точки используются как опорные узлы для построения, маркировки или оснастки (DIVIDE, MEASURE).",
      examples: ["ТОЧКА 100,50", "PO 0,0"],
      seeAlso: ["LINE", "CIRCLE"],
    },
    en: {
      short: "Creates a POINT entity at the specified location.",
      long: "The POINT command creates a POINT primitive — a standalone entity with a single 3D location. The on-screen appearance of the marker is governed by the PDMODE system variable (0 = single dot, 2 = +, 3 = X, 4 = vertical tick; +32 inscribes a circle, +64 inscribes a square) and PDSIZE controls its size. Points are most often used as reference nodes for construction, snapping (NODE OSnap), or as the output of DIVIDE / MEASURE.",
      examples: ["POINT 100,50", "PO 0,0"],
      seeAlso: ["LINE", "CIRCLE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.point.label",
    labelKeyEn: "command.point.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "PT",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-point", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── CIRCLE ─────────────────────────────────────
const CIRCLE: CommandDescriptor = {
  id: "CIRCLE",
  category: "drawing",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["CIRCLE", "C"], ru: ["ОКРУЖНОСТЬ", "КРУГ"] },
  commandTypeLiterals: ["geometry/create-circle"],
  draftCommandId: "CIRCLE",
  draftSteps: [
    {
      stepId: "circle-center",
      promptKeyRu: "prompt.circle.center",
      promptKeyEn: "prompt.circle.center",
      expects: "point",
    },
    {
      stepId: "circle-radius",
      promptKeyRu: "prompt.circle.radius",
      promptKeyEn: "prompt.circle.radius",
      expects: "distance",
      keywords: ["D"],
    },
    {
      stepId: "circle-diameter",
      promptKeyRu: "prompt.circle.diameter",
      promptKeyEn: "prompt.circle.diameter",
      expects: "distance",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся окружность.",
        en: "Space identifier where the circle is created.",
      },
    },
    {
      name: "center",
      type: "point3d",
      required: true,
      description: {
        ru: "Центр окружности (x, y, z).",
        en: "Center point of the circle (x, y, z).",
      },
    },
    {
      name: "radius",
      type: "number",
      required: true,
      description: {
        ru: "Радиус окружности (положительное число).",
        en: "Radius of the circle (positive number).",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт окружность по центру и радиусу.",
      long: "Команда ОКРУЖНОСТЬ создаёт окружность как самостоятельную сущность CIRCLE. По умолчанию запрашиваются центр и радиус, но вторым шагом можно ввести Д (Диаметр), чтобы задать диаметр вместо радиуса. Окружность хранится без сегментации; рендер аппроксимирует её гладкой кривой в зависимости от масштаба.",
      examples: ["ОКРУЖНОСТЬ 50,50 25", "C 0,0 D 100"],
      seeAlso: ["ARC", "REVCLOUD"],
    },
    en: {
      short: "Creates a circle from a center point and a radius.",
      long: "The CIRCLE command creates a circle as a standalone CIRCLE entity. By default the prompts are center and radius, but on the radius step you can type D (Diameter) to enter a diameter instead. The circle is stored as a true mathematical primitive (center + radius) and the renderer approximates it as a smooth curve based on the current zoom level.",
      examples: ["CIRCLE 50,50 25", "C 0,0 D 100"],
      seeAlso: ["ARC", "REVCLOUD"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.circle.label",
    labelKeyEn: "command.circle.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "C",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-circle", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── DONUT ─────────────────────────────────────
// Phase D3 — AutoCAD-parity DONUT: closed LWPOLYLINE encoding of a filled
// annulus. Two bulge=1 vertices form two semicircles on the midline radius;
// constant per-segment width (group code 40/41 = (outerD-innerD)/2) makes
// the painted band span innerR..outerR exactly. AutoCAD prompts the user
// for inside diameter, then outside diameter, then loops on the center
// click until <Esc>/<Enter>.
const DONUT: CommandDescriptor = {
  id: "DONUT",
  category: "drawing",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: {
    en: ["DONUT", "DO", "DOUGHNUT"],
    ru: ["КОЛЬЦО", "КОЛ"],
  },
  commandTypeLiterals: ["geometry/create-donut"],
  draftCommandId: "DONUT",
  draftSteps: [
    {
      stepId: "donut-inner-d",
      promptKeyRu: "prompt.donut.inner_d",
      promptKeyEn: "prompt.donut.inner_d",
      expects: "distance",
    },
    {
      stepId: "donut-outer-d",
      promptKeyRu: "prompt.donut.outer_d",
      promptKeyEn: "prompt.donut.outer_d",
      expects: "distance",
    },
    {
      stepId: "donut-center",
      promptKeyRu: "prompt.donut.center",
      promptKeyEn: "prompt.donut.center",
      expects: "point",
      keywords: ["EXIT"],
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся кольцо.",
        en: "Space identifier where the donut is created.",
      },
    },
    {
      name: "center",
      type: "point3d",
      required: true,
      description: {
        ru: "Центр кольца (x, y, z).",
        en: "Center point of the donut (x, y, z).",
      },
    },
    {
      name: "innerDiameter",
      type: "number",
      required: true,
      description: {
        ru: "Внутренний диаметр кольца (≥ 0). При нуле — сплошной диск.",
        en: "Inside diameter of the donut (≥ 0). Zero means a solid disk.",
      },
    },
    {
      name: "outerDiameter",
      type: "number",
      required: true,
      description: {
        ru: "Внешний диаметр кольца (положительное число).",
        en: "Outside diameter of the donut (positive number).",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт кольцо (заливаемая замкнутая полилиния).",
      long: "Команда КОЛЬЦО создаёт замкнутую LWPOLYLINE с двумя дугами bulge=1 и постоянной шириной сегмента, равной половине разности внешнего и внутреннего диаметров. Запрашиваются внутренний диаметр, внешний диаметр и центр; центр запрашивается в цикле — каждое нажатие создаёт новое кольцо с теми же диаметрами, выход по Enter/Esc. При нулевом внутреннем диаметре получается сплошной залитый диск.",
      examples: [
        "КОЛЬЦО 0.5 1.0 50,50",
        "КОЛ 0 1.5 0,0",
      ],
      seeAlso: ["CIRCLE", "PLINE"],
    },
    en: {
      short: "Creates a donut (filled closed polyline).",
      long: "The DONUT command creates a closed LWPOLYLINE with two bulge=1 arcs and a constant segment width equal to half the difference between outside and inside diameters. The user is prompted for inside diameter, outside diameter, and a center; the center prompt loops so additional donuts can be placed with the same diameters, ended via Enter/Esc. An inside diameter of zero produces a solid filled disk.",
      examples: ["DONUT 0.5 1.0 50,50", "DO 0 1.5 0,0"],
      seeAlso: ["CIRCLE", "PLINE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.donut.label",
    labelKeyEn: "command.donut.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "DO",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-donut", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── ARC ────────────────────────────────────────
const ARC: CommandDescriptor = {
  id: "ARC",
  category: "drawing",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["ARC", "A"], ru: ["ДУГА"] },
  commandTypeLiterals: ["geometry/create-arc"],
  draftCommandId: "ARC",
  draftSteps: [
    {
      stepId: "arc-start",
      promptKeyRu: "prompt.arc.start",
      promptKeyEn: "prompt.arc.start",
      expects: "point",
    },
    {
      stepId: "arc-second",
      promptKeyRu: "prompt.arc.second",
      promptKeyEn: "prompt.arc.second",
      expects: "point",
    },
    {
      stepId: "arc-end",
      promptKeyRu: "prompt.arc.end",
      promptKeyEn: "prompt.arc.end",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся дуга.",
        en: "Space identifier where the arc is created.",
      },
    },
    {
      name: "center",
      type: "point3d",
      required: true,
      description: {
        ru: "Центр дуги (x, y, z).",
        en: "Center point of the arc (x, y, z).",
      },
    },
    {
      name: "radius",
      type: "number",
      required: true,
      description: {
        ru: "Радиус дуги (положительное число).",
        en: "Radius of the arc (positive number).",
      },
    },
    {
      name: "startAngle",
      type: "number",
      required: true,
      description: {
        ru: "Начальный угол дуги в радианах против часовой стрелки от оси X.",
        en: "Start angle of the arc in radians, counter-clockwise from the X axis.",
      },
    },
    {
      name: "endAngle",
      type: "number",
      required: true,
      description: {
        ru: "Конечный угол дуги в радианах против часовой стрелки от оси X.",
        en: "End angle of the arc in radians, counter-clockwise from the X axis.",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт дугу по трём точкам (начало, точка на дуге, конец).",
      long: "Команда ДУГА создаёт дугу как самостоятельную сущность ARC, заданную центром, радиусом и парой углов. По умолчанию используется метод '3 точки' — указываются начальная точка, любая промежуточная точка дуги и конечная точка; центр и радиус вычисляются как окружность, проходящая через эти три точки. Дуга всегда отрисовывается против часовой стрелки от начального угла к конечному.",
      examples: ["ДУГА 0,0 50,50 100,0", "A 0,0 25,40 50,0"],
      seeAlso: ["CIRCLE", "PLINE"],
    },
    en: {
      short: "Creates an arc from three points (start, on-arc, end).",
      long: "The ARC command creates an arc as a standalone ARC entity defined by a center, a radius and a start/end angle pair. The default method is 3-point — pick a start, any intermediate point on the arc and an end; center and radius are derived as the circle through those three points. Arcs are always drawn counter-clockwise from start angle to end angle.",
      examples: ["ARC 0,0 50,50 100,0", "A 0,0 25,40 50,0"],
      seeAlso: ["CIRCLE", "PLINE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.arc.label",
    labelKeyEn: "command.arc.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "A",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-arc", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── ELLIPSE ────────────────────────────────────
// Phase D4 — AutoCAD-parity ELLIPSE creation. The descriptor only models the
// "Center" mode (3 picks: center, major-axis endpoint, minor-axis distance).
// AutoCAD's "Axis-end" and "Arc" sub-modes are deferred — partial ellipses
// are reachable via the property panel by editing startParam / endParam on
// an existing entity. The reducer derives ratio = minor / major and snaps
// the ratio to (0, 1] (swap when typed minor > major) — see geometryReducer
// `case "geometry/create-ellipse"`.
const ELLIPSE: CommandDescriptor = {
  id: "ELLIPSE",
  category: "drawing",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  // RU AutoCAD-CIS default: ЭЛЛИПС / ЭЛ. The "T'KKBGC" alias is the
  // RU-keyboard mistype of "ELLIPSE" — supported so muscle-memory works
  // when the user forgets to switch keyboard layout.
  aliases: {
    en: ["ELLIPSE", "EL"],
    ru: ["ЭЛЛИПС", "ЭЛ", "T'KKBGC"],
  },
  commandTypeLiterals: ["geometry/create-ellipse"],
  draftCommandId: "ELLIPSE",
  draftSteps: [
    {
      stepId: "ellipse-center",
      promptKeyRu: "prompt.ellipse.center",
      promptKeyEn: "prompt.ellipse.center",
      expects: "point",
    },
    {
      stepId: "ellipse-major-end",
      promptKeyRu: "prompt.ellipse.major_end",
      promptKeyEn: "prompt.ellipse.major_end",
      expects: "point",
    },
    {
      stepId: "ellipse-minor-distance",
      promptKeyRu: "prompt.ellipse.minor_distance",
      promptKeyEn: "prompt.ellipse.minor_distance",
      expects: "distance",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся эллипс.",
        en: "Space identifier where the ellipse is created.",
      },
    },
    {
      name: "center",
      type: "point3d",
      required: true,
      description: {
        ru: "Центр эллипса (x, y, z).",
        en: "Center point of the ellipse (x, y, z).",
      },
    },
    {
      name: "majorAxisEnd",
      type: "point3d",
      required: true,
      description: {
        ru: "Конечная точка большой оси эллипса в мировых координатах. Вектор (majorAxisEnd - center) определяет направление и длину большой полуоси.",
        en: "Endpoint of the ellipse's major axis in world coordinates. The vector (majorAxisEnd - center) defines the major semi-axis direction and length.",
      },
    },
    {
      name: "minorDistance",
      type: "number",
      required: true,
      description: {
        ru: "Длина малой полуоси (положительное число). Если значение превышает длину большой полуоси, AutoCAD меняет роли осей — итоговое отношение всегда в (0, 1].",
        en: "Minor semi-axis length (positive number). When this exceeds the major semi-axis length, AutoCAD swaps axis roles so the resulting ratio is always in (0, 1].",
      },
    },
    {
      name: "startParam",
      type: "number",
      required: false,
      description: {
        ru: "Начальный параметр в радианах (по умолчанию 0). Полный эллипс: startParam=0, endParam=2π.",
        en: "Start parameter in radians (default 0). Full ellipse: startParam=0, endParam=2π.",
      },
    },
    {
      name: "endParam",
      type: "number",
      required: false,
      description: {
        ru: "Конечный параметр в радианах (по умолчанию 2π).",
        en: "End parameter in radians (default 2π).",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт эллипс по центру и осям.",
      long: "Команда ЭЛЛИПС создаёт эллипс как самостоятельную сущность ELLIPSE. По умолчанию используется режим «Центр»: указываются центр, конечная точка большой оси и длина другой оси. Большая ось задаётся вектором от центра до второй точки; её угол берётся как atan2(dy, dx). Если введённая малая ось длиннее большой, AutoCAD меняет роли осей. В DXF эллипс хранится через group codes 10/20 (центр), 11/21 (вектор большой полуоси, относительно центра), 40 (отношение minor/major), 41/42 (start/end параметры в радианах).",
      examples: ["ЭЛЛИПС 0,0 50,0 30", "EL 100,100 150,100 20"],
      seeAlso: ["CIRCLE", "ARC"],
    },
    en: {
      short: "Creates an ellipse from a center and two axis specifications.",
      long: "The ELLIPSE command creates an ellipse as a standalone ELLIPSE entity. The default mode is Center: pick center, pick the major-axis endpoint, then pick or type the distance to the other axis. The major-axis vector is (endpoint - center); its rotation is atan2(dy, dx). If the typed minor distance exceeds the major-axis length, AutoCAD swaps the roles so the major axis is always the longer one (ratio always in (0, 1]). In DXF the ellipse is stored via group codes 10/20 (center), 11/21 (major-axis vector relative to center), 40 (minor/major ratio), and 41/42 (start/end parameters in radians).",
      examples: ["ELLIPSE 0,0 50,0 30", "EL 100,100 150,100 20"],
      seeAlso: ["CIRCLE", "ARC"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.ellipse.label",
    labelKeyEn: "command.ellipse.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "EL",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-ellipse", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── PLINE ──────────────────────────────────────
const PLINE: CommandDescriptor = {
  id: "PLINE",
  category: "drawing",
  status: "implemented",
  aiCallable: true,
  aliases: {
    en: ["PLINE", "PL", "POLYLINE", "LWPOLYLINE"],
    ru: ["ПЛИНИЯ", "ПЛ"],
  },
  commandTypeLiterals: ["geometry/create-lwpolyline"],
  draftCommandId: "PLINE",
  draftSteps: [
    {
      stepId: "polyline-first-point",
      promptKeyRu: "prompt.polyline.first",
      promptKeyEn: "prompt.polyline.first",
      expects: "point",
    },
    {
      stepId: "polyline-next-point",
      promptKeyRu: "prompt.polyline.next",
      promptKeyEn: "prompt.polyline.next",
      expects: "point",
      keywords: ["C", "U"],
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся полилиния.",
        en: "Space identifier where the polyline is created.",
      },
    },
    {
      name: "vertices",
      type: "object",
      required: true,
      description: {
        ru: "Массив вершин (минимум 2). Каждая вершина: x, y, bulge (выпуклость дугового сегмента), startWidth, endWidth.",
        en: "Array of vertices (minimum 2). Each vertex: x, y, bulge (arc-segment curvature), startWidth, endWidth.",
      },
    },
    {
      name: "closed",
      type: "boolean",
      required: true,
      description: {
        ru: "Замкнута ли полилиния (true — добавляется замыкающий сегмент от последней вершины к первой).",
        en: "Whether the polyline is closed (true — an implicit closing segment is added from the last vertex back to the first).",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создаёт двумерную полилинию (LWPOLYLINE) с прямыми и/или дуговыми сегментами.",
      long: "Команда ПЛИНИЯ создаёт цельную сущность LWPOLYLINE — последовательность вершин, связанных линейными или дуговыми сегментами. В отличие от ОТРЕЗКА (LINE), полилиния является ОДНОЙ сущностью и редактируется как целое (PEDIT, GRIP-редактирование, ОБРЕЗАТЬ применяется к смежным сегментам). Каждая вершина может иметь bulge (выпуклость) для дугового сегмента и независимые startWidth/endWidth для переменной толщины. На втором шаге введите C для замыкания полилинии или U для отмены последней вершины.",
      examples: [
        "ПЛИНИЯ 0,0 100,0 100,50 0,50 C",
        "PL 0,0 @100,0 @0,50 @-100,0 C",
      ],
      seeAlso: ["LINE", "RECTANG", "REVCLOUD"],
    },
    en: {
      short:
        "Creates a 2D polyline (LWPOLYLINE) with straight and/or arc segments.",
      long: "The PLINE command creates a single LWPOLYLINE entity — a sequence of vertices joined by linear or arc segments. Unlike LINE, the polyline is ONE entity and can be edited as a whole (PEDIT, grip editing, TRIM cuts adjacent segments). Each vertex carries an optional bulge (curvature) for arc segments plus independent startWidth/endWidth for variable thickness. On the next-point step, type C to close the polyline or U to undo the last vertex.",
      examples: [
        "PLINE 0,0 100,0 100,50 0,50 C",
        "PL 0,0 @100,0 @0,50 @-100,0 C",
      ],
      seeAlso: ["LINE", "RECTANG", "REVCLOUD"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.polyline.label",
    labelKeyEn: "command.polyline.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "PL",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/create-lwpolyline",
      file: REDUCER_GEOMETRY,
    },
  ],
};

// ───────────────────────────── RECTANG ────────────────────────────────────
// RECTANG is materialised as a 4-vertex closed LWPOLYLINE — same command
// type as PLINE. The draft state machine constructs the four corner
// vertices from the two picks before dispatching.
const RECTANG: CommandDescriptor = {
  id: "RECTANG",
  category: "drawing",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: {
    en: ["RECTANG", "RECTANGLE", "REC"],
    ru: ["ПРЯМОУГОЛЬНИК", "ПРЯМОУГ", "ПРЯМ"],
  },
  commandTypeLiterals: ["geometry/create-lwpolyline"],
  draftCommandId: "RECTANG",
  draftSteps: [
    {
      stepId: "rectangle-first-corner",
      promptKeyRu: "prompt.rectangle.first",
      promptKeyEn: "prompt.rectangle.first",
      expects: "point",
    },
    {
      stepId: "rectangle-second-corner",
      promptKeyRu: "prompt.rectangle.second",
      promptKeyEn: "prompt.rectangle.second",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся прямоугольник.",
        en: "Space identifier where the rectangle is created.",
      },
    },
    {
      name: "vertices",
      type: "object",
      required: true,
      description: {
        ru: "Четыре угловые вершины прямоугольника (вычисляются из двух противоположных углов).",
        en: "Four corner vertices of the rectangle (computed from the two opposite-corner picks).",
      },
    },
    {
      name: "closed",
      type: "boolean",
      required: true,
      description: {
        ru: "Всегда true — прямоугольник представлен замкнутой полилинией.",
        en: "Always true — the rectangle is stored as a closed polyline.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создаёт прямоугольник как замкнутую LWPOLYLINE по двум противоположным углам.",
      long: "Команда ПРЯМОУГОЛЬНИК создаёт прямоугольник, выровненный по осям, как замкнутую LWPOLYLINE с четырьмя вершинами. Запрашиваются две диагонально противоположные точки; остальные две вершины вычисляются автоматически. Поскольку результат — обычная полилиния, его можно редактировать через PEDIT, GRIP-редактирование и обрабатывать стандартными командами модификации (ОБРЕЗАТЬ, СМЕЩЕНИЕ, СОПРЯЖЕНИЕ).",
      examples: ["ПРЯМОУГОЛЬНИК 0,0 100,50", "REC 0,0 @50,30"],
      seeAlso: ["PLINE", "LINE"],
    },
    en: {
      short:
        "Creates an axis-aligned rectangle as a closed LWPOLYLINE from two opposite corners.",
      long: "The RECTANG command creates an axis-aligned rectangle as a closed four-vertex LWPOLYLINE. It prompts for two diagonally-opposite corner picks; the other two vertices are derived automatically. Because the result is an ordinary polyline you can edit it via PEDIT, grip editing, and the usual modification commands (TRIM, OFFSET, FILLET).",
      examples: ["RECTANG 0,0 100,50", "REC 0,0 @50,30"],
      seeAlso: ["PLINE", "LINE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.rectangle.label",
    labelKeyEn: "command.rectangle.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "REC",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/create-lwpolyline",
      file: REDUCER_GEOMETRY,
    },
  ],
};

// ───────────────────────────── HATCH ──────────────────────────────────────
const HATCH: CommandDescriptor = {
  id: "HATCH",
  category: "drawing",
  status: "implemented",
  aiCallable: true,
  // D5 — "BH" is App.tsx's shell-keyboard shorthand for BHATCH, present in
  // shellKeyboardCommandAliases but missing here.
  aliases: { en: ["HATCH", "H", "BHATCH", "BH"], ru: ["ШТРИХ", "ШТРИХОВКА"] },
  commandTypeLiterals: ["geometry/create-hatch"],
  draftCommandId: "HATCH",
  draftSteps: [
    {
      stepId: "hatch-pattern",
      promptKeyRu: "prompt.hatch.pattern",
      promptKeyEn: "prompt.hatch.pattern",
      expects: "text",
    },
    // Phase E — mode prompt comes after pattern entry. Default branch
    // (Internal point pick) lands on hatch-pick-internal-point; the
    // legacy rectangular flow is reachable via "S"/"R" keyword.
    {
      stepId: "hatch-mode-prompt",
      promptKeyRu: "prompt.hatch.mode",
      promptKeyEn: "prompt.hatch.mode",
      expects: "keyword",
    },
    {
      stepId: "hatch-pick-internal-point",
      promptKeyRu: "prompt.hatch.pick_internal",
      promptKeyEn: "prompt.hatch.pick_internal",
      expects: "point",
    },
    {
      stepId: "hatch-pick-additional",
      promptKeyRu: "prompt.hatch.pick_additional",
      promptKeyEn: "prompt.hatch.pick_additional",
      expects: "point",
    },
    {
      stepId: "hatch-first-corner",
      promptKeyRu: "prompt.hatch.first",
      promptKeyEn: "prompt.hatch.first",
      expects: "point",
    },
    {
      stepId: "hatch-second-corner",
      promptKeyRu: "prompt.hatch.second",
      promptKeyEn: "prompt.hatch.second",
      expects: "point",
    },
    {
      stepId: "hatch-ready",
      promptKeyRu: "prompt.hatch.confirm",
      promptKeyEn: "prompt.hatch.confirm",
      expects: "keyword",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся штриховка.",
        en: "Space identifier where the hatch is created.",
      },
    },
    {
      name: "boundaryCorner1",
      type: "point2d",
      required: true,
      description: {
        ru: "Первый угол прямоугольной области штриховки (x, y).",
        en: "First corner of the rectangular hatch boundary (x, y).",
      },
    },
    {
      name: "boundaryCorner2",
      type: "point2d",
      required: true,
      description: {
        ru: "Противоположный угол прямоугольной области штриховки (x, y).",
        en: "Opposite corner of the rectangular hatch boundary (x, y).",
      },
    },
    {
      name: "patternName",
      type: "string",
      required: false,
      defaultValue: "ANSI31",
      description: {
        ru: "Имя образца штриховки (например, ANSI31, SOLID, DOTS). По умолчанию ANSI31.",
        en: "Hatch pattern name (e.g. ANSI31, SOLID, DOTS). Defaults to ANSI31.",
      },
    },
    {
      name: "patternScale",
      type: "number",
      required: false,
      defaultValue: "1",
      description: {
        ru: "Масштаб образца штриховки (положительное число). По умолчанию 1.",
        en: "Hatch pattern scale (positive number). Defaults to 1.",
      },
    },
    {
      name: "patternAngle",
      type: "number",
      required: false,
      defaultValue: "0",
      description: {
        ru: "Угол поворота образца штриховки в градусах. По умолчанию 0.",
        en: "Hatch pattern rotation angle in degrees. Defaults to 0.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Заполняет замкнутую область штриховым образцом или сплошной заливкой.",
      long: "Команда ШТРИХОВКА (HATCH/BHATCH) создаёт сущность HATCH, которая заполняет указанную область заданным образцом. AutoCAD-режим Pick: после выбора образца кликните внутри замкнутой области, и система автоматически определит границу (отрезки, дуги, окружности, полилинии). Альтернативный режим Rectangle: задайте область по двум углам прямоугольника. Поддерживаются как линейчатые образцы (ANSI31, ANSI32, ...) с масштабом и углом, так и сплошная заливка (SOLID).",
      examples: ["ШТРИХ ANSI31 0,0 100,100", "H SOLID 10,10 50,50"],
      seeAlso: ["PLINE", "RECTANG"],
    },
    en: {
      short: "Fills a closed region with a hatch pattern or solid fill.",
      long: "The HATCH command (also BHATCH) creates a HATCH entity that fills a specified region with a given pattern. Pick mode (default, AutoCAD parity): after entering a pattern the user clicks inside a closed region and the system automatically traces the boundary (lines, arcs, circles, polylines). Rectangle mode: region is defined by two corner picks. Supports line patterns (ANSI31, ANSI32, ...) with scale and angle, plus solid fill (SOLID).",
      examples: ["HATCH ANSI31 0,0 100,100", "H SOLID 10,10 50,50"],
      seeAlso: ["PLINE", "RECTANG"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.hatch.label",
    labelKeyEn: "command.hatch.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "H",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-hatch", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── HATCHEDIT ──────────────────────────────────
// AutoCAD-parity HATCHEDIT (acad.pgp: `HE *HATCHEDIT`). Selects a placed HATCH
// entity and edits its already-wired pattern fields (name / scale / angle /
// hatch style / pattern type / associative). CLI-driven (prompts) like
// HATCH-create — NOT a tabbed dialog (that is a separate future PR). Mirrors
// ARRAYEDIT one-for-one: a selection step then field prompts, committing
// `geometry/hatchedit` which the geometry reducer routes through the SAME
// `translateExtendedEntityPropertyChanges` → `patchEntityById` path the
// Properties palette uses.
//
// RU alias: `РЕДШТРИХ` is the AutoCAD-CIS canonical localized name for HATCHEDIT
// (РЕД + the HATCH base ШТРИХ, consistent with HATCH RU base = ШТРИХ at the
// HATCH descriptor above); `ШТРИХРЕД` follows the in-repo base+РЕД convention
// (ARRAYEDIT=МАССИВРЕД). Both are exposed, mirroring MLEADEREDIT
// (МВЫНОСКАРЕД / МЛИНИЯРЕД) and EATTEDIT (УЛАТРЕД / УЛРЕДАТР).
//
// DEFERRED (mutation layer absent — NOT stubbed, see docs/test-gaps/cmd-hatch.md):
// gradient-fill edit, boundary Add/Remove, Inherit-Properties origin.
const HATCHEDIT: CommandDescriptor = {
  id: "HATCHEDIT",
  category: "drawing",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["HATCHEDIT", "HE"], ru: ["РЕДШТРИХ", "ШТРИХРЕД"] },
  commandTypeLiterals: ["geometry/hatchedit"],
  draftCommandId: "HATCHEDIT",
  draftSteps: [
    {
      stepId: "hatchedit-select",
      promptKeyRu: "prompt.hatchedit.select",
      promptKeyEn: "prompt.hatchedit.select",
      expects: "selection",
    },
    {
      stepId: "hatchedit-pattern",
      promptKeyRu: "prompt.hatchedit.pattern",
      promptKeyEn: "prompt.hatchedit.pattern",
      expects: "text",
    },
    {
      stepId: "hatchedit-scale",
      promptKeyRu: "prompt.hatchedit.scale",
      promptKeyEn: "prompt.hatchedit.scale",
      expects: "distance",
    },
    {
      stepId: "hatchedit-angle",
      promptKeyRu: "prompt.hatchedit.angle",
      promptKeyEn: "prompt.hatchedit.angle",
      expects: "angle",
    },
  ],
  parameters: [
    {
      name: "entityId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор редактируемой штриховки (HATCH).",
        en: "Identifier of the HATCH entity being edited.",
      },
    },
    {
      name: "patternName",
      type: "string",
      required: false,
      description: {
        ru: "Новое имя образца штриховки (например, ANSI31, SOLID). Необязательно.",
        en: "New hatch pattern name (e.g. ANSI31, SOLID). Optional.",
      },
    },
    {
      name: "patternScale",
      type: "number",
      required: false,
      description: {
        ru: "Новый масштаб образца (положительное число). Необязательно.",
        en: "New pattern scale (positive number). Optional.",
      },
    },
    {
      name: "patternAngle",
      type: "number",
      required: false,
      description: {
        ru: "Новый угол поворота образца в градусах. Необязательно.",
        en: "New pattern rotation angle in degrees. Optional.",
      },
    },
    {
      name: "hatchStyle",
      type: "number",
      required: false,
      description: {
        ru: "Стиль штриховки (DXF 75: 0=Обычный, 1=Внешний, 2=Игнорировать). Необязательно.",
        en: "Hatch style (DXF 75: 0=Normal, 1=Outer, 2=Ignore). Optional.",
      },
    },
    {
      name: "patternType",
      type: "number",
      required: false,
      description: {
        ru: "Тип образца (DXF 76: 0=Пользовательский, 1=Стандартный, 2=Заказной). Необязательно.",
        en: "Pattern type (DXF 76: 0=User-defined, 1=Predefined, 2=Custom). Optional.",
      },
    },
    {
      name: "associative",
      type: "boolean",
      required: false,
      description: {
        ru: "Ассоциативность штриховки с границей (DXF 71). Необязательно.",
        en: "Whether the hatch stays associative with its boundary (DXF 71). Optional.",
      },
    },
    // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2, GitHub review round 1) —
    // the command registry is the ONE-TRUTH parameter list AI/tool-calling
    // and docs/reference/command-registry.json are generated from; a typed
    // geometry/hatchedit call carrying doubleLine (added to the Zod contract
    // + the reducer/inverse raw-scalar paths in this same slice) stayed
    // undiscoverable here otherwise, even though the forward command genuinely
    // supports it.
    {
      name: "doubleLine",
      type: "boolean",
      required: false,
      description: {
        ru: "Двойная штриховка — второй набор линий под 90° к первому (DXF 77). Действует только для пользовательского типа образца (patternType=0). Необязательно.",
        en: "Double-line hatch — a second line family at 90° to the first (DXF 77). Only meaningful for a User-defined pattern type (patternType=0). Optional.",
      },
    },
  ],
  help: {
    ru: {
      short: "Редактирование параметров существующей штриховки (HATCH).",
      long: "Команда HATCHEDIT (РЕДШТРИХ / ШТРИХРЕД, alias HE) выбирает размещённую штриховку и редактирует её параметры через Командный движок (geometry/hatchedit), тем же путём, что и палитра Свойств (translateExtendedEntityPropertyChanges → patchEntityById), поэтому правка попадает в undo/redo и экспорт DXF. Интерактивные запросы команды HATCHEDIT/HE в командной строке охватывают имя образца, масштаб и угол. Стиль штриховки (DXF 75), тип образца (DXF 76), ассоциативность (DXF 71) и двойная штриховка (DXF 77, только для пользовательского типа образца) команда geometry/hatchedit тоже поддерживает, но задавать их следует через палитру Свойств либо типизированный/ИИ-вызов geometry/hatchedit — отдельных интерактивных запросов для них нет. Передаётся минимум одно поле (пустая правка — холостой ход). Градиентная заливка, добавление/удаление контуров и наследование свойств в этой версии НЕ редактируются (отложено — см. docs/test-gaps/cmd-hatch.md).",
      examples: ["HATCHEDIT", "РЕДШТРИХ", "HE"],
      seeAlso: ["HATCH"],
    },
    en: {
      short: "Edit parameters of an existing hatch (HATCH) entity.",
      long: "The HATCHEDIT command (РЕДШТРИХ / ШТРИХРЕД, alias HE) selects a placed hatch and edits its parameters through the Command Engine (geometry/hatchedit) via the SAME path the Properties palette uses (translateExtendedEntityPropertyChanges → patchEntityById), so the edit is carried by undo/redo and DXF export. The interactive HATCHEDIT/HE command-line prompts cover pattern name, scale and angle. Hatch style (DXF 75), pattern type (DXF 76), associativity (DXF 71) and the double-line flag (DXF 77, only meaningful for a User-defined pattern type) are also supported by the geometry/hatchedit command, but are set via the Properties palette or a typed/AI geometry/hatchedit dispatch — there is no interactive prompt for them. At least one field must be supplied (an empty edit is a no-op). Gradient fill, boundary Add/Remove and Inherit-Properties origin are NOT editable in this slice (deferred — see docs/test-gaps/cmd-hatch.md).",
      examples: ["HATCHEDIT", "HE"],
      seeAlso: ["HATCH"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.hatchedit.label",
    labelKeyEn: "command.hatchedit.label",
    ribbonTab: "modify",
    ribbonSection: "modify",
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/hatchedit", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── POLYGON ───────────────────────────────────
// Phase D2 — AutoCAD-parity POLYGON: regular N-sided polygon represented
// as a closed LWPOLYLINE with N equal sides (no bulge). The user is
// prompted in sequence for the number of sides (3..1024), inscribed /
// circumscribed mode, the polygon centre, and finally the radius (typed
// or clicked). The Edge sub-flow (build the polygon from one of its
// sides) is intentionally deferred — the AutoCAD prompt offers it but
// real-world usage of POLYGON is dominated by the radius-driven flow.
const POLYGON: CommandDescriptor = {
  id: "POLYGON",
  category: "drawing",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: {
    en: ["POLYGON", "POL"],
    ru: ["ПОЛИГОН", "ПОЛ", "МНГ", "МНОГОУГОЛЬНИК"],
  },
  commandTypeLiterals: ["geometry/create-lwpolyline"],
  draftCommandId: "POLYGON",
  draftSteps: [
    {
      stepId: "polygon-sides-input",
      promptKeyRu: "prompt.polygon.sides",
      promptKeyEn: "prompt.polygon.sides",
      expects: "distance",
    },
    {
      stepId: "polygon-mode-prompt",
      promptKeyRu: "prompt.polygon.mode",
      promptKeyEn: "prompt.polygon.mode",
      expects: "keyword",
      keywords: ["INSCRIBED", "CIRCUMSCRIBED"],
    },
    {
      stepId: "polygon-center",
      promptKeyRu: "prompt.polygon.center",
      promptKeyEn: "prompt.polygon.center",
      expects: "point",
    },
    {
      stepId: "polygon-radius",
      promptKeyRu: "prompt.polygon.radius",
      promptKeyEn: "prompt.polygon.radius",
      expects: "distance",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся полигон.",
        en: "Space identifier where the polygon is created.",
      },
    },
    {
      name: "vertices",
      type: "object",
      required: true,
      description: {
        ru: "N вершин правильного многоугольника, вычисляемых из центра, радиуса и режима (вписан / описан).",
        en: "N vertices of the regular polygon, computed from centre, radius and inscribed / circumscribed mode.",
      },
    },
    {
      name: "closed",
      type: "boolean",
      required: true,
      description: {
        ru: "Всегда true — полигон представлен замкнутой полилинией.",
        en: "Always true — the polygon is stored as a closed polyline.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создаёт правильный многоугольник как замкнутую LWPOLYLINE по числу сторон, центру и радиусу.",
      long: "Команда ПОЛИГОН создаёт правильный N-угольник (3..1024 сторон) как замкнутую LWPOLYLINE с равными сторонами без bulge. Запрашиваются число сторон, режим [Вписан/Описан] (по умолчанию Вписан), центр и радиус. В режиме Вписан вершины лежат на окружности заданного радиуса; в режиме Описан середины сторон лежат на этой окружности (фактический описанный радиус = радиус / cos(π/N)). Поскольку результат — обычная полилиния, многоугольник можно редактировать через PEDIT, GRIP и стандартные команды модификации.",
      examples: [
        "ПОЛИГОН 6 В 0,0 50",
        "ПОЛ 3 О 100,100 25",
      ],
      seeAlso: ["RECTANG", "CIRCLE", "PLINE"],
    },
    en: {
      short:
        "Creates a regular N-sided polygon as a closed LWPOLYLINE from sides, centre and radius.",
      long: "The POLYGON command creates a regular N-sided polygon (3..1024 sides) as a closed LWPOLYLINE with equal-length sides and zero bulge. The user is prompted for the number of sides, [Inscribed/Circumscribed] mode (default Inscribed), centre, and radius. In Inscribed mode the vertices lie on the radius circle; in Circumscribed mode the midpoints of the polygon's edges lie on the radius circle (the effective circumradius is therefore radius / cos(π/N)). Because the result is an ordinary polyline you can edit it via PEDIT, grip editing, and the usual modification commands.",
      examples: ["POLYGON 6 I 0,0 50", "POL 3 C 100,100 25"],
      seeAlso: ["RECTANG", "CIRCLE", "PLINE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.polygon.label",
    labelKeyEn: "command.polygon.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "POL",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/create-lwpolyline",
      file: REDUCER_GEOMETRY,
    },
  ],
};

// ───────────────────────────── REVCLOUD ───────────────────────────────────
const REVCLOUD: CommandDescriptor = {
  id: "REVCLOUD",
  category: "drawing",
  status: "implemented",
  aiCallable: true,
  // D5 — "RC" / "RV" are App.tsx's shell-keyboard shorthands, present in
  // shellKeyboardCommandAliases but missing here.
  aliases: { en: ["REVCLOUD", "RC", "RV"], ru: ["ОБЛАКО", "ОБЛАЧКО"] },
  commandTypeLiterals: ["geometry/create-revcloud"],
  draftCommandId: "REVCLOUD",
  // REVCLOUD-MODES — the first prompt carries the AutoCAD option set
  // [Arc length/Object/Rectangular/Polygonal/Freehand/Style/Modify];
  // the sub-mode steps below are entered via those keywords.
  draftSteps: [
    {
      stepId: "revcloud-first-corner",
      promptKeyRu: "prompt.revcloud.first",
      promptKeyEn: "prompt.revcloud.first",
      expects: "point",
      keywords: ["A", "O", "R", "P", "F", "S", "M"],
    },
    {
      stepId: "revcloud-second-corner",
      promptKeyRu: "prompt.revcloud.second",
      promptKeyEn: "prompt.revcloud.second",
      expects: "point",
    },
    {
      stepId: "revcloud-arc-length",
      promptKeyRu: "prompt.revcloud.arc_length",
      promptKeyEn: "prompt.revcloud.arc_length",
      expects: "distance",
    },
    {
      stepId: "revcloud-style",
      promptKeyRu: "prompt.revcloud.style",
      promptKeyEn: "prompt.revcloud.style",
      expects: "keyword",
      keywords: ["N", "C"],
    },
    {
      stepId: "revcloud-polygonal-next",
      promptKeyRu: "prompt.revcloud.polygonal_next",
      promptKeyEn: "prompt.revcloud.polygonal_next",
      expects: "point",
    },
    {
      stepId: "revcloud-freehand-path",
      promptKeyRu: "prompt.revcloud.freehand",
      promptKeyEn: "prompt.revcloud.freehand",
      expects: "point",
    },
    {
      stepId: "revcloud-object-pick",
      promptKeyRu: "prompt.revcloud.object_pick",
      promptKeyEn: "prompt.revcloud.object_pick",
      expects: "selection",
    },
    {
      stepId: "revcloud-object-reverse",
      promptKeyRu: "prompt.revcloud.object_reverse",
      promptKeyEn: "prompt.revcloud.object_reverse",
      expects: "keyword",
      keywords: ["Y", "N"],
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся облако правок.",
        en: "Space identifier where the revision cloud is created.",
      },
    },
    {
      name: "creationMode",
      type: "enum",
      enumValues: ["Rectangular", "Polygonal", "Freehand", "Object"],
      required: false,
      defaultValue: "Rectangular",
      description: {
        ru: "Способ построения облака: Rectangular (два угла), Polygonal (вершины полигона), Freehand (путь от руки; сохраняется как Polygonal) или Object (преобразование замкнутого объекта).",
        en: "Creation method: Rectangular (two corners), Polygonal (polygon vertices), Freehand (guided path; persisted as Polygonal) or Object (convert an existing closed entity).",
      },
    },
    {
      name: "corner1",
      type: "point2d",
      required: false,
      description: {
        ru: "Первый угол прямоугольной области облака правок (x, y). Обязателен в режиме Rectangular.",
        en: "First corner of the revision cloud bounding rectangle (x, y). Required for Rectangular mode.",
      },
    },
    {
      name: "corner2",
      type: "point2d",
      required: false,
      description: {
        ru: "Противоположный угол прямоугольной области облака правок (x, y). Обязателен в режиме Rectangular.",
        en: "Opposite corner of the revision cloud bounding rectangle (x, y). Required for Rectangular mode.",
      },
    },
    {
      name: "vertices",
      type: "object",
      required: false,
      description: {
        ru: "Вершины облака (минимум 3) для режимов Polygonal и Freehand — дуги следуют вдоль каждого сегмента.",
        en: "Cloud vertices (3 minimum) for Polygonal and Freehand modes — arcs follow each segment.",
      },
    },
    {
      name: "sourceEntityId",
      type: "entityId",
      required: false,
      description: {
        ru: "Режим Object: идентификатор замкнутого исходного объекта (круг, эллипс, замкнутая полилиния или сплайн). Исходный объект заменяется облаком (как в AutoCAD).",
        en: "Object mode: id of the closed source entity (circle, ellipse, closed polyline or spline). The source is replaced by the cloud (AutoCAD behaviour).",
      },
    },
    {
      name: "reverseDirection",
      type: "boolean",
      required: false,
      defaultValue: "false",
      description: {
        ru: "Режим Object: ответ на запрос 'Изменить направление [Да/Нет]' — переворачивает сторону выпуклости дуг.",
        en: "Object mode: the 'Reverse direction [Yes/No]' answer — flips which side the arc bulges point to.",
      },
    },
    {
      name: "revisionCloudStyle",
      type: "enum",
      enumValues: ["Normal", "Calligraphy"],
      required: false,
      defaultValue: "Normal",
      description: {
        ru: "Стиль дуги облака: Normal или Calligraphy (параметр Стиль команды ОБЛАКО).",
        en: "Cloud arc style: Normal or Calligraphy (the REVCLOUD Style option).",
      },
    },
    {
      name: "arcChordLength",
      type: "number",
      required: false,
      defaultValue: "1",
      description: {
        ru: "Длина хорды одной дуги облака. Меньше — больше дуг и более 'плотное' облако. По умолчанию 1; задаётся параметром 'Длина дуги'.",
        en: "Chord length of a single revision-cloud arc. Smaller value yields more arcs (denser cloud). Defaults to 1; set via the 'Arc length' option.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создаёт облако правок (REVCLOUD) для выделения изменённых областей чертежа.",
      long: "Команда ОБЛАЧКО (REVCLOUD) создаёт замкнутую полилинию, состоящую из последовательных дуговых сегментов, образующих характерное 'облако' для пометки участков чертежа, требующих внимания. Первый запрос содержит параметры [Длина дуги/Объект/ПРямоугольное/ПОЛигональное/От Руки/Стиль/Изменить]: Прямоугольное — по двум углам; Полигональное — по вершинам; От руки — по пути перекрестья с автозамыканием у начальной точки; Объект — преобразование замкнутого круга, эллипса, полилинии или сплайна (исходный объект заменяется, затем запрос 'Изменить направление [Да/Нет]'). Параметр 'Длина дуги' задаёт arcChordLength (плотность дуг), 'Стиль' — Normal/Calligraphy. Выбранный способ построения сохраняется между вызовами (аналог REVCLOUDCREATEMODE).",
      examples: ["ОБЛАЧКО 0,0 100,50", "REVCLOUD 10,10 200,150"],
      seeAlso: ["PLINE", "RECTANG"],
    },
    en: {
      short:
        "Creates a revision cloud (REVCLOUD) to mark up changed regions of a drawing.",
      long: "The REVCLOUD command creates a closed polyline made of successive arc segments forming the characteristic 'cloud' shape used to flag areas of a drawing that need attention. The first prompt offers [Arc length/Object/Rectangular/Polygonal/Freehand/Style/Modify]: Rectangular draws by two corners; Polygonal by picked vertices; Freehand follows the guided crosshairs and closes at the start point; Object converts a closed circle, ellipse, polyline or spline (the source is replaced, then 'Reverse direction [Yes/No]' is prompted). 'Arc length' sets arcChordLength (cloud density), 'Style' picks Normal/Calligraphy. The chosen creation method persists across invocations (REVCLOUDCREATEMODE behaviour).",
      examples: ["REVCLOUD 0,0 100,50", "REVCLOUD 10,10 200,150"],
      seeAlso: ["PLINE", "RECTANG"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.revcloud.label",
    labelKeyEn: "command.revcloud.label",
    ribbonTab: "annotate",
    ribbonSection: "ribbon.section.drafting",
    glyph: "RC",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-revcloud", file: REDUCER_GEOMETRY },
  ],
};

// ─────────────────────────── REVCLOUDPROPERTIES ────────────────────────────
// REVCLOUD-PROPS-EDITOR (fullstack §5.1, A8/E-3). Opens the revision-cloud
// special-properties editor (the CC-3 `SpecialEditorHost` form registered in
// `SPECIAL_EDITOR_REGISTRY["Revision Cloud"]`) for a selected revision cloud —
// edit Cloud style / arc length (min + approximate) / creation mode, then Apply
// regenerates the cloud arcs through `properties/apply`. EXPLICIT owned CLI
// routing (A8/E-3: "route here … NOT dialog-only") — the literal is a real
// registry command, not merely reachable by clicking a dialog.
//
// A dialog-opening command like DSETTINGS / DIMSTYLE: no EditorCommand of its own
// (the edit dispatches `properties/apply` from inside the form via the host), so
// `commandTypeLiterals` / `reducerLocations` are empty and `aiCallable` is false
// (per the §10.1 matrix, property/dialog editors are not AI-driven headlessly).
//
// RU alias `ОБЛАКОСВ` = ОБЛАКО (the REVCLOUD RU base) + СВ (СВойства), matching
// the in-repo property-command convention.
const REVCLOUDPROPERTIES: CommandDescriptor = {
  id: "REVCLOUDPROPERTIES",
  category: "modify",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["REVCLOUDPROPERTIES"], ru: ["ОБЛАКОСВ"] },
  commandTypeLiterals: [],
  // Selection-driven like AutoCAD's Properties palette (the command edits the
  // currently-selected Revision Cloud) — NO positional args. An earlier draft
  // advertised an optional `entityId` param, but the command-line router matches
  // the alias against the whole trimmed input, and the handler only reads
  // `selectedEntities`, so `REVCLOUDPROPERTIES rc1` never matched and the param
  // was dead advertisement (@codex #487 P2). Removed rather than parsed to keep
  // one clear selection-based contract.
  parameters: [],
  help: {
    ru: {
      short:
        "Открывает редактор свойств облака правок (стиль дуги, длина дуги, способ построения).",
      long: "Команда ОБЛАКОСВ (REVCLOUDPROPERTIES) открывает специальный редактор свойств выбранного облака правок: стиль дуги (Обычный/Каллиграфия), минимальная и приблизительная длина дуги, а также способ построения (Прямоугольное/Полигональное/Объект). Значения читаются из типизированной модели (DEFUSE S15 PropertyDescriptor), а изменения применяются через безопасный контур командой properties/apply — приблизительная длина дуги управляет плотностью дуг облака, поэтому её правка регенерирует дуги при нажатии «Применить»/«ОК» (как REGEN в AutoCAD). Аналог редактирования облака правок через палитру свойств AutoCAD.",
      examples: ["ОБЛАКОСВ", "REVCLOUDPROPERTIES"],
      seeAlso: ["REVCLOUD"],
    },
    en: {
      short:
        "Opens the revision-cloud property editor (arc style, arc length, creation mode).",
      long: "The REVCLOUDPROPERTIES (ОБЛАКОСВ) command opens the special-properties editor for a selected revision cloud: arc style (Normal/Calligraphy), minimum and approximate arc length, and creation mode (Rectangular/Polygonal/Object). Values are read from the typed model (DEFUSE S15 PropertyDescriptor) and edits are applied through the safe contour via properties/apply — the approximate arc length drives the cloud's arc density, so editing it regenerates the arcs on Apply/OK (AutoCAD's implicit REGEN). Mirrors editing a revision cloud through AutoCAD's Properties palette.",
      examples: ["REVCLOUDPROPERTIES", "ОБЛАКОСВ"],
      seeAlso: ["REVCLOUD"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.revcloudproperties.label",
    labelKeyEn: "command.revcloudproperties.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

// ───────────────────────────── WIPEOUT ────────────────────────────────────
// AutoCAD WIPEOUT — closed polygon with opaque background fill that hides
// entities rendered beneath it. Phase H integration: rectangular two-corner
// flow (first-pass; polygon-pick deferred). Already wired through state
// machine, reducer, contracts and storeHelpers — this descriptor closes the
// command-registry gap so typed input (WIPEOUT/ВЫРЕЗ) reaches startDraftSession.
const WIPEOUT: CommandDescriptor = {
  id: "WIPEOUT",
  category: "drawing",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: { en: ["WIPEOUT", "WO"], ru: ["ВЫРЕЗ", "ВЫРЕЗАТЬ"] },
  commandTypeLiterals: ["geometry/create-wipeout"],
  draftCommandId: "WIPEOUT",
  draftSteps: [
    {
      stepId: "wipeout-first-corner",
      promptKeyRu: "prompt.wipeout.first_corner",
      promptKeyEn: "prompt.wipeout.first_corner",
      expects: "point",
    },
    {
      stepId: "wipeout-second-corner",
      promptKeyRu: "prompt.wipeout.second_corner",
      promptKeyEn: "prompt.wipeout.second_corner",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся выреза.",
        en: "Space identifier where the wipeout is created.",
      },
    },
    {
      name: "vertices",
      type: "string",
      required: true,
      description: {
        ru: "JSON-массив вершин замкнутого многоугольника (как минимум 3 точки x,y).",
        en: "JSON array of closed-polygon vertices (at least 3 points x,y).",
      },
    },
    {
      name: "frame",
      type: "boolean",
      required: false,
      defaultValue: "true",
      description: {
        ru: "Флаг отображения рамки выреза (WIPEOUTFRAME=1 — показ рамки).",
        en: "Frame visibility flag (WIPEOUTFRAME=1 — show frame).",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создаёт замкнутый многоугольник с непрозрачной заливкой, скрывающий нижележащие сущности.",
      long: "Команда ВЫРЕЗ (WIPEOUT) создаёт сущность WIPEOUT — замкнутый многоугольник с белой непрозрачной заливкой, который скрывает все сущности, расположенные за ним. Используется для маскировки или временного скрытия частей чертежа без их фактического удаления. В первой фазе поддерживается прямоугольный режим — задайте два диагональных угла. Полигональный режим (вершины кликами) будет добавлен в следующих фазах. Системная переменная WIPEOUTFRAME управляет видимостью контура (1 — показ, 0 — скрытие).",
      examples: ["ВЫРЕЗ 0,0 100,50", "WIPEOUT 10,10 200,150"],
      seeAlso: ["HATCH", "REVCLOUD"],
    },
    en: {
      short:
        "Creates a closed polygon with opaque fill that hides entities behind it.",
      long: "The WIPEOUT command creates a WIPEOUT entity — a closed polygon with white opaque background fill that hides all entities rendered behind it. Used for masking or temporarily hiding parts of a drawing without actually deleting them. The first-pass implementation supports rectangular mode — specify two diagonal corners. Polygon-pick mode (vertices clicked one by one) will land in later phases. The WIPEOUTFRAME system variable controls outline visibility (1 — show, 0 — hide).",
      examples: ["WIPEOUT 0,0 100,50", "WIPEOUT 10,10 200,150"],
      seeAlso: ["HATCH", "REVCLOUD"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.wipeout.label",
    labelKeyEn: "command.wipeout.label",
    ribbonTab: "annotate",
    ribbonSection: "ribbon.section.drafting",
    glyph: "WO",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-wipeout", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── SPLINE ─────────────────────────────────────
// AutoCAD SPLINE — full-fidelity NURBS authoring. Phase H first-pass: only
// fit-points mode is wired through the UI (state machine + this descriptor),
// matching how AutoCAD's SPLINE command opens with "Specify first point or
// [Method]" defaulting to fit-point mode. Control-point mode is reserved as
// a future sub-flow (M keyword) but not exposed yet — the entity / DXF I/O
// support both modes today.
const SPLINE: CommandDescriptor = {
  id: "SPLINE",
  category: "drawing",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: { en: ["SPLINE", "SPL"], ru: ["СПЛАЙН"] },
  commandTypeLiterals: ["geometry/create-spline"],
  draftCommandId: "SPLINE",
  draftSteps: [
    {
      stepId: "spline-fit-first",
      promptKeyRu: "prompt.spline.fit_first",
      promptKeyEn: "prompt.spline.fit_first",
      expects: "point",
    },
    {
      stepId: "spline-fit-next",
      promptKeyRu: "prompt.spline.fit_next",
      promptKeyEn: "prompt.spline.fit_next",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "spaceId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор пространства, в котором создаётся сплайн.",
        en: "Space identifier where the spline is created.",
      },
    },
    {
      name: "fitPoints",
      type: "string",
      required: true,
      description: {
        ru: "JSON-массив точек прохождения кривой (fit-points mode, не менее 2).",
        en: "JSON array of fit-points the curve passes through (fit-points mode, at least 2).",
      },
    },
    {
      name: "degree",
      type: "number",
      required: false,
      defaultValue: "3",
      description: {
        ru: "Степень NURBS (по умолчанию 3 — кубический сплайн).",
        en: "NURBS degree (default 3 — cubic spline).",
      },
    },
    {
      name: "closed",
      type: "boolean",
      required: false,
      defaultValue: "false",
      description: {
        ru: "Замкнутый сплайн (true: периодический; по умолчанию false).",
        en: "Closed spline (true: periodic; default false).",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создаёт NURBS-сплайн через указанные точки (fit-points mode) или по контрольному полигону.",
      long: "Команда СПЛАЙН (SPLINE) создаёт NURBS-сущность с гладкой непрерывной кривизной. AutoCAD поддерживает два режима построения: Fit-points — кривая проходит через заданные точки; Control-points — кривая аппроксимирует контрольный полигон (NURBS). На данном этапе через CLI доступен только Fit-points: пользователь кликает точки одну за другой, Enter завершает построение. По умолчанию степень сплайна = 3 (кубический); закрытие через keyword Close. Опциональные касательные на концах задаются keyword Tangent.",
      examples: ["СПЛАЙН 0,0 50,50 100,0 150,50", "SPLINE 10,10 50,80 100,30"],
      seeAlso: ["PLINE", "ARC"],
    },
    en: {
      short:
        "Creates a NURBS spline through fit-points or by a control-point polygon.",
      long: "The SPLINE command creates a NURBS entity with smooth continuous curvature. AutoCAD supports two construction modes: Fit-points — the curve passes through specified points; Control-points — the curve approximates a control polygon (NURBS). The CLI flow currently exposes Fit-points mode only: the user clicks points one by one and presses Enter to finish. Default degree = 3 (cubic spline); closing handled via the Close keyword. Optional end tangents may be specified via the Tangent keyword.",
      examples: ["SPLINE 0,0 50,50 100,0 150,50", "SPLINE 10,10 50,80 100,30"],
      seeAlso: ["PLINE", "ARC"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.spline.label",
    labelKeyEn: "command.spline.label",
    ribbonTab: "draw",
    ribbonSection: "ribbon.section.drafting",
    glyph: "SP",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/create-spline", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── EXPORT ─────────────────────────────────────
export const DRAWING_DESCRIPTORS: readonly CommandDescriptor[] = [
  ARC,
  CIRCLE,
  DONUT,
  ELLIPSE,
  HATCH,
  HATCHEDIT,
  LINE,
  PLINE,
  POINT,
  POLYGON,
  RECTANG,
  REVCLOUD,
  REVCLOUDPROPERTIES,
  SPLINE,
  WIPEOUT,
];
